import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Package,
  User,
  Store,
  DollarSign,
  Clock,
  ShoppingCart,
  XCircle,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Tag,
  Hash,
  Layers
} from 'lucide-react'
import { cartService, CartSession, CartItem } from '@/services/cartService'
import useAppConfigStore from '@/stores/appConfigStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

const CartDetailPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { systemConfig } = useAppConfigStore()
  
  const [cart, setCart] = useState<CartSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expiringItemId, setExpiringItemId] = useState<string | null>(null)

  useEffect(() => {
    const loadCart = async () => {
      if (!sessionId) return
      
      try {
        setLoading(true)
        const response = await cartService.getById(sessionId)
        
        if (response.success && response.data) {
          setCart(response.data)
        } else {
          setError('Panier non trouvé')
        }
      } catch (err) {
        console.error('Erreur lors du chargement du panier:', err)
        setError('Erreur lors du chargement du panier')
      } finally {
        setLoading(false)
      }
    }
    
    loadCart()
  }, [sessionId])

  const formatCurrency = (amount: number) => {
    const currencyCode = systemConfig?.default_currency || 'MAD'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const formatTimeRemaining = (expiresAt: string) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diffMs = expires.getTime() - now.getTime()
    
    if (diffMs <= 0) return 'Expiré'
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffDays > 0) {
      return `${diffDays}j ${diffHours % 24}h`
    }
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m`
    }
    return `${diffMinutes}m`
  }

  const getStatusBadgeVariant = (status: CartSession['status']) => {
    switch (status) {
      case 'active': return 'default'
      case 'expired': return 'destructive'
      case 'converted': return 'outline'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: CartSession['status']) => {
    switch (status) {
      case 'active': return 'Actif'
      case 'expired': return 'Expiré'
      case 'converted': return 'Converti'
      default: return status
    }
  }

  const getStatusIcon = (status: CartSession['status']) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-3 w-3 mr-1" />
      case 'expired': return <XCircle className="h-3 w-3 mr-1" />
      case 'converted': return <AlertCircle className="h-3 w-3 mr-1" />
      default: return null
    }
  }

  const handleForceExpireCart = async () => {
    if (!sessionId || !cart || cart.status !== 'active') return
    
    if (confirm('Êtes-vous sûr de vouloir forcer l\'expiration de ce panier entier ?')) {
      try {
        const response = await cartService.forceExpire(sessionId)
        if (response.success) {
          alert('Panier expiré avec succès')
          // Reload cart data
          const reloadResponse = await cartService.getById(sessionId)
          if (reloadResponse.success && reloadResponse.data) {
            setCart(reloadResponse.data)
          }
        } else {
          alert(`Erreur: ${response.error}`)
        }
      } catch (err) {
        alert('Erreur lors de l\'expiration du panier')
      }
    }
  }

  const handleForceExpireItem = async (itemId: string) => {
    if (!sessionId) return
    
    setExpiringItemId(itemId)
    if (confirm('Êtes-vous sûr de vouloir forcer l\'expiration de cet élément ?')) {
      try {
        // Note: This would require a new API endpoint for expiring individual items
        // For now, we'll use the cart expiration endpoint
        alert('Fonctionnalité à implémenter: expiration d\'élément individuel')
      } catch (err) {
        alert('Erreur lors de l\'expiration de l\'élément')
      } finally {
        setExpiringItemId(null)
      }
    } else {
      setExpiringItemId(null)
    }
  }

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Paniers d\'achat', href: '/carts' },
    { label: cart?.sessionId?.substring(0, 8) + '...' || 'Détails', href: '#' }
  ]

  const headerActions = [
    {
      label: 'Retour',
      icon: <ArrowLeft className="h-4 w-4 mr-2" />,
      onClick: () => navigate('/carts'),
      variant: 'outline' as const
    },
    {
      label: 'Forcer expiration',
      icon: <XCircle className="h-4 w-4 mr-2" />,
      onClick: handleForceExpireCart,
      variant: 'destructive' as const,
      disabled: !cart || cart.status !== 'active'
    }
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Chargement..."
          subtitle="Chargement des détails du panier"
          breadcrumbs={breadcrumbs}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <Skeleton className="h-8 w-1/3" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <Skeleton className="h-6 w-1/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (error || !cart) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Erreur"
          subtitle={error || 'Panier non trouvé'}
          breadcrumbs={breadcrumbs}
          actions={[
            {
              label: 'Retour',
              icon: <ArrowLeft className="h-4 w-4 mr-2" />,
              onClick: () => navigate('/carts'),
              variant: 'outline' as const
            }
          ]}
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{error || 'Panier non trouvé'}</h3>
              <p className="text-gray-500 mb-6">
                Le panier demandé n'existe pas ou n'est plus disponible.
              </p>
              <Button onClick={() => navigate('/carts')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour aux paniers
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={`Panier ${cart.sessionId.substring(0, 8)}...`}
        subtitle={`Détails du panier d'achat - ${getStatusLabel(cart.status)}`}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Cart details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cart information */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <ShoppingCart className="h-5 w-5 mr-2 text-gray-500" />
                <h3 className="text-lg font-semibold">Informations du panier</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <Hash className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="text-gray-500">ID de session :</span>
                  </div>
                  <div className="font-mono text-sm bg-gray-50 p-2 rounded">
                    {cart.sessionId}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <User className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="text-gray-500">Utilisateur:</span>
                  </div>
                  <div className="font-medium">
                    {cart.userEmail || 'Invité'}
                    {cart.userId && (
                      <div className="text-xs text-gray-500 font-mono mt-1">
                        ID: {cart.userId.substring(0, 8)}...
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <Store className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="text-gray-500">Commerçant:</span>
                  </div>
                  <div className="font-medium">
                    {cart.merchantName || 'N/D'}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <Badge variant={getStatusBadgeVariant(cart.status)} className="inline-flex items-center">
                      {getStatusIcon(cart.status)}
                      {getStatusLabel(cart.status)}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-500">
                    {cart.status === 'active' ? 'Panier actif' : 
                     cart.status === 'expired' ? 'Panier expiré' : 
                     'Panier converti en commande'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items in cart */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <Package className="h-5 w-5 mr-2 text-gray-500" />
                <h3 className="text-lg font-semibold">Articles dans le panier</h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Article</TableHead>
                      <TableHead>Quantité</TableHead>
                      <TableHead>Prix</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cart.items.map((item: CartItem) => {
                      const itemTotal = item.price * item.quantity
                      return (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {item.type === 'product' ? 'Produit' : 'Offre'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.id.substring(0, 8)}...</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <Layers className="h-4 w-4 mr-2 text-gray-400" />
                              {item.quantity}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <Tag className="h-4 w-4 mr-2 text-gray-400" />
                              {formatCurrency(item.price)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              {formatCurrency(itemTotal)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleForceExpireItem(item.id)}
                              disabled={cart.status !== 'active' || expiringItemId === item.id}
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Expirer
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
              
              {cart.items.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun article dans ce panier</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column - Summary and actions */}
        <div className="space-y-6">
          {/* Cart summary */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <DollarSign className="h-5 w-5 mr-2 text-gray-500" />
                <h3 className="text-lg font-semibold">Résumé</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Articles totaux:</span>
                  <span className="font-medium">{cart.totalItems}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Valeur totale:</span>
                  <span className="font-medium text-lg">{formatCurrency(cart.totalValue)}</span>
                </div>
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Créé le:</span>
                    <span className="text-sm">{formatDate(cart.createdAt)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600">Dernière activité:</span>
                    <span className="text-sm">{formatDate(cart.lastActivity)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600">Expire le:</span>
                    <div className="text-sm">
                      <div>{formatDate(cart.expiresAt)}</div>
                      <div className="text-orange-600 font-medium">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {formatTimeRemaining(cart.expiresAt)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick actions */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-5 w-5 mr-2 text-gray-500" />
                <h3 className="text-lg font-semibold">Actions rapides</h3>
              </div>
              <div className="space-y-3">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleForceExpireCart}
                  disabled={cart.status !== 'active'}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Forcer l'expiration du panier
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/carts')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour aux paniers
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.location.reload()}
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Actualiser la page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default CartDetailPage